<template>
  <v-container class="pb-12">
    <v-row>
      <v-col cols="12" class="subheader-sticky">
        <div
          style="
            display: flex !important;
            justify-content: space-between;
            flex-wrap: wrap;
            align-items: center;
            gap: 12px;
          "
        >
          <div class="d-flex align-center" style="gap: 8px">
            <v-btn
              variant="text"
              size="small"
              prepend-icon="mdi-arrow-left"
              class="text-grey-darken-2"
              @click="goBackToProducts"
            >
              Back
            </v-btn>
            <h2
              class="text-h5 font-weight-medium d-flex align-center flex-column flex-sm-row"
              style="margin: 0; gap: 12px"
            >
              Edit Product Settings
              <v-chip v-if="productFriendlyName" color="primary" variant="tonal" size="small">
                {{ productFriendlyName }}
              </v-chip>
            </h2>
          </div>
          <div class="d-flex ga-3 text-copy">
            <v-btn
              data-testid="toggle-product-btn"
              variant="text"
              size="small"
              class="text-grey-darken-2"
              :prepend-icon="productActive ? 'mdi-toggle-switch-off' : 'mdi-toggle-switch'"
              @click="openConfirmToggle()"
            >
              {{ productActive ? 'Disable' : 'Enable' }}
            </v-btn>
            <v-btn
              data-testid="remove-product-btn"
              v-if="canRemoveProduct && !productActive"
              variant="text"
              size="small"
              color="error"
              prepend-icon="mdi-delete"
              @click="openRemoveDialog()"
            >
              Remove
            </v-btn>
          </div>
        </div>

        <v-alert
          v-if="!productActive"
          type="warning"
          variant="tonal"
          density="comfortable"
          class="mb-3"
        >
          This product is currently disabled. You can enable it to edit settings and resume usage.
        </v-alert>
      </v-col>
    </v-row>
    <div class="mt-4">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-spacer></v-spacer>
          <v-btn
            v-if="hasVisibleFields"
            variant="text"
            size="small"
            prepend-icon="mdi-content-copy"
            @click="copyJson($event)"
          >
            Copy full JSON
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div v-if="!hasVisibleFields" class="text-grey-darken-1">
            This product currently has no customizable settings.
          </div>
          <template v-for="(val, key) in formData" :key="String(key)">
            <v-row class="mb-2" v-if="!isActivationKey(String(key))">
              <v-col
                cols="12"
                md="4"
                class="text-caption text-grey-darken-1"
                style="text-transform: capitalize"
              >
                {{ toLabel(String(key)) }}
              </v-col>
              <v-col cols="12" md="8">
                <template v-if="val === true || val === false">
                  <v-switch
                    :model-value="val"
                    @update:modelValue="(v: any) => onTopBooleanChange(String(key), Boolean(v))"
                    inset
                    color="primary"
                    hide-details="auto"
                    :disabled="!productActive"
                  />
                </template>
                <template v-else-if="typeof val === 'number'">
                  <v-text-field
                    :model-value="val"
                    @update:modelValue="(v: string | number) => onTopNumberChange(String(key), v)"
                    type="number"
                    variant="outlined"
                    density="comfortable"
                    hide-details="auto"
                    :readonly="!productActive"
                  />
                </template>
                <template v-else-if="val === null || typeof val === 'object'">
                  <v-textarea
                    :model-value="fieldTextMap[String(key)]"
                    @update:modelValue="(v: string) => onTopJsonChange(String(key), v)"
                    label="JSON value"
                    auto-grow
                    variant="outlined"
                    density="comfortable"
                    :error-messages="jsonFieldErrors[String(key)]"
                    hide-details="auto"
                    rows="8"
                    :readonly="!productActive"
                  />
                </template>
                <template v-else>
                  <v-text-field
                    :model-value="String(val ?? '')"
                    @update:modelValue="(v: string) => onTopStringChange(String(key), v)"
                    variant="outlined"
                    density="comfortable"
                    hide-details="auto"
                    :readonly="!productActive"
                  />
                </template>
              </v-col>
            </v-row>
          </template>
        </v-card-text>
      </v-card>
    </div>
  </v-container>
  <!-- Floating footer actions with fade-in -->
  <v-fade-transition>
    <div v-show="footerVisible && productActive && hasVisibleFields" class="floating-footer">
      <v-container class="py-2 d-flex justify-end ga-2">
        <v-btn data-testid="cancel-btn" variant="tonal" color="grey" @click="cancelEdit">
          Cancel
        </v-btn>
        <v-btn
          data-testid="save-btn"
          color="primary"
          @click="submitEdit"
          :disabled="!isDirty || hasJsonErrors"
        >
          Save Changes
        </v-btn>
      </v-container>
    </div>
  </v-fade-transition>
  <v-snackbar v-model="snackbar" :color="snackbarColor" location="bottom center" timeout="2000">
    {{ snackbarText }}
  </v-snackbar>

  <!-- Confirm Enable/Disable Dialog -->
  <v-dialog v-model="confirmToggleDialog" max-width="500" data-testid="toggle-dialog">
    <v-card>
      <v-card-title>Confirm {{ productActive ? 'Disable' : 'Enable' }}</v-card-title>
      <v-card-text>
        <p class="mb-2">
          Are you sure you want to {{ productActive ? 'disable' : 'enable' }} this product?
        </p>
        <v-alert
          v-if="productActive && toggleChildren.length > 0"
          type="info"
          variant="tonal"
          density="compact"
          class="mt-3"
        >
          <div class="text-body-2">
            <strong>Note:</strong>
            This will also disable {{ toggleChildren.length }} child product(s):
            <ul class="mt-2 ml-4">
              <li v-for="child in toggleChildren.slice(0, 5)" :key="child" class="text-caption">
                {{ child }}
              </li>
              <li v-if="toggleChildren.length > 5" class="text-caption">
                ...and {{ toggleChildren.length - 5 }} more
              </li>
            </ul>
          </div>
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          data-testid="toggle-dialog-cancel"
          variant="text"
          color="grey"
          @click="confirmToggleDialog = false"
        >
          Cancel
        </v-btn>
        <v-btn data-testid="toggle-dialog-confirm" color="primary" @click="confirmToggle">
          Confirm
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Remove Product Confirmation Dialog -->
  <v-dialog v-model="removeDialog" max-width="500" data-testid="remove-dialog">
    <v-card>
      <v-card-title class="text-h6">Remove Product</v-card-title>
      <v-card-text>
        <p class="mb-4">Are you sure you want to remove this product from the account?</p>
        <v-alert
          v-if="productChildren.length > 0"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-3"
        >
          <strong>Warning:</strong>
          This product has {{ productChildren.length }} child product{{
            productChildren.length > 1 ? 's' : ''
          }}
          that will also be removed:
          <ul class="mt-2 ml-4">
            <li v-for="child in productChildren.slice(0, 5)" :key="child.path">
              {{ child.productFriendlyName || child.productName }}
            </li>
            <li v-if="productChildren.length > 5">... and {{ productChildren.length - 5 }} more</li>
          </ul>
        </v-alert>
        <v-alert type="warning" variant="tonal" density="compact">
          This action cannot be undone. All product configuration data for this account will be
          permanently deleted.
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          data-testid="remove-dialog-cancel"
          color="grey"
          variant="text"
          :disabled="removingProduct"
          @click="removeDialog = false"
        >
          Cancel
        </v-btn>
        <v-btn
          data-testid="remove-dialog-confirm"
          color="error"
          variant="flat"
          :loading="removingProduct"
          @click="removeProductFromAccount"
        >
          Remove Product{{
            productChildren.length > 0
              ? ` & ${productChildren.length} Child${productChildren.length > 1 ? 'ren' : ''}`
              : ''
          }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import {
  fetchAccountProductsAdmin,
  updateProductForAccount,
  toggleProductForAccount,
  getParentChild,
  fetchAccountsProduct,
  deleteProductFromAccount,
} from '@/api/accountsAdmin'
import { useUserStore } from '@/stores/userStore'
import {
  prepareImportFdds2ConfigForSave,
  prepareImportFdds2FormConfig,
} from '@/utils/importFdds2AccountConfig'

// ---- Pretty labels & field detection ----
const toLabel = (key: string) =>
  key
    .replace(/[_\-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b[a-z]/g, (m) => m.toUpperCase())
    .trim()

const isSecretKey = (k: string) => /password|token|secret|apikey|api_key|key$/i.test(k)
const isBoolKey = (k: string) => /^(is|has)[A-Z_]|enabled?$|active$/i.test(k)
const isPortKey = (k: string) => /port$/i.test(k)
const isHostKey = (k: string) => /^(host|hostname|server)$/i.test(k)
const isUrlKey = (k: string) => /(url|endpoint|webhook|callback)$/i.test(k)
const isActivationKey = (k: string) => /^(enable|enabled|isactive|is_active|active)$/i.test(k)

// Maps to track UI state for secrets
const showSecret = reactive<Record<string, boolean>>({})

// Simple numeric guard (>= 0; empty → 0)
const coerceNonNegative = (v: any) => {
  const n = Number(v)
  if (Number.isFinite(n) && n >= 0) return n
  return 0
}

// Basic field rules (extend as needed)
const hostRules = [(v: string) => !v || /^[a-z0-9.\-:_]+$/i.test(v) || 'Invalid host']
const portRules = [(v: any) => v === '' || (Number(v) >= 0 && Number(v) <= 65535) || 'Port 0–65535']
const urlRules = [(v: string) => !v || /^https?:\/\//i.test(v) || 'Must start with http(s)://']

// Utility: when rendering nested arrays/objects, make stable keys
const pathKey = (path: (string | number)[]) => path.join('.')

// Reuse: your existing helpers for numbers
const onNumberInputUnified = (obj: any, key: string) => {
  obj[key] = coerceNonNegative(obj[key])
}

// Deeply remove activation keys from any config object/array
const stripActivationKeysDeep = (input: any): any => {
  if (Array.isArray(input)) {
    return input.map((v) => stripActivationKeysDeep(v))
  }
  if (input && typeof input === 'object') {
    const out: Record<string, any> = {}
    Object.keys(input).forEach((k) => {
      const v = input[k]
      if (typeof v === 'boolean' && isActivationKey(k)) {
        return // skip activation flags
      }
      out[k] = stripActivationKeysDeep(v)
    })
    return out
  }
  return input
}

// Router and route
const router = useRouter()
const route = useRoute()
const accountName = String(route.params.name || '')
// Prefer query param for product identifier, with param fallback
const productId = String((route.query.query as any) || (route.params.productId as any) || '')
const showTextGoBack = ref(false)
const dataInitialized = ref(false)
const productActive = ref(true)
const productFriendlyName = ref<string>('')

// Reactive state
const formData = reactive<Record<string, any>>({})
const jsonError = ref<string | undefined>(undefined)
const jsonText = computed(() => JSON.stringify(formData, null, 2))
// Per-field JSON support
const fieldTextMap = reactive<Record<string, string>>({})
const jsonFieldErrors = reactive<Record<string, string | undefined>>({})
const hasJsonErrors = computed(() => Object.values(jsonFieldErrors).some(Boolean))
const isEditMode = ref(true)
const originalData = ref<Record<string, any>>({})
const storedProductData = ref<Record<string, any>>({})
const syntheticUseIsConditionalOriginal = ref(false)

// Show controls only when at least one non-activation field is present
const hasVisibleFields = computed(() => {
  return Object.keys(formData).some((k) => !isActivationKey(String(k)))
})

const isArrayOfStringOrNumber = (arr: any[]) => {
  return arr.every((item) => typeof item === 'string' || typeof item === 'number')
}
const enforceMinValue = (key: string) => {
  if (formData[key] < 0 || isNaN(formData[key])) {
    formData[key] = 0
  }
}
const cancelEdit = () => {
  try {
    Object.keys(originalData.value).forEach((key) => {
      formData[key] = JSON.parse(JSON.stringify(originalData.value[key]))
    })
  } catch {}
  router.push(`/account-admin/${String(accountName)}#products`)
}

// Fetch product data for the given account
const loadProductData = async () => {
  try {
    const data = await fetchAccountProductsAdmin(String(accountName), String(productId)) // Fetch product data
    productActive.value = data?.isActive !== false
    storedProductData.value = JSON.parse(JSON.stringify(data))
    const prepared = prepareImportFdds2FormConfig(productId, data)
    syntheticUseIsConditionalOriginal.value = prepared.syntheticUseIsConditionalOriginal
    initializeFormData(prepared.config)

    // Try to get the friendly name from the account products list
    try {
      const accountProducts = await fetchAccountsProduct(String(accountName))
      const product = accountProducts.find(
        (p: any) =>
          p.productName === productId ||
          p.productId === productId ||
          p.simpleProductName === productId
      )
      if (product) {
        productFriendlyName.value = product.productFriendlyName || productId
      } else {
        productFriendlyName.value = productId
      }
    } catch (e) {
      console.log('Could not fetch product friendly name, using ID')
      productFriendlyName.value = productId
    }
  } catch (error) {
    console.error('Failed to load product data:', error)
    productActive.value = false
    productFriendlyName.value = productId
    dataInitialized.value = true
  }
}

// Initialize form data for the view page
const initializeFormData = (product: Record<string, any>) => {
  originalData.value = JSON.parse(JSON.stringify(product)) // Store deep copy

  Object.keys(product).forEach((key) => {
    if (Array.isArray(product[key])) {
      formData[key] = product[key].map((item: any) =>
        typeof item === 'object' ? reactive(item) : item
      )
    } else if (typeof product[key] === 'object') {
      formData[key] = reactive({ ...product[key] })
    } else {
      formData[key] = product[key]
    }
    // initialize per-field text
    const v = product[key]
    fieldTextMap[key] =
      v !== null && typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v ?? '')
  })

  showTextGoBack.value = Object.keys(formData).length < 2
  dataInitialized.value = true
}
const onNumberInput = (key: string) => {
  let value = formData[key]

  if (typeof value === 'string') {
    const parsedValue = parseFloat(value)
    formData[key] = !isNaN(parsedValue) ? Math.max(0, parsedValue) : 0
  } else {
    formData[key] = Math.max(0, value)
  }
}
const enforceNonEmptyNonNegativeNumber2 = (key: string, subKey: string) => {
  let value = formData[key][subKey]

  // Convert value to a number if it's a string
  if (typeof value === 'string') {
    value = parseFloat(value)
  }

  // If value is empty, null, or NaN → reset to 0
  if (value === '' || value === null || isNaN(value)) {
    formData[key][subKey] = 0
  }

  // If value is negative → reset to 0
  if (value < 0) {
    formData[key][subKey] = 0
  }
}
const enforceNonEmptyNonNegativeNumber = (key: string, index: number, subKey: string) => {
  let value = formData[key][index][subKey]

  // Convert value to a number if it's a string
  if (typeof value === 'string') {
    value = parseFloat(value)
  }

  // If value is empty, null, or NaN → reset to 0
  if (value === '' || value === null || isNaN(value)) {
    formData[key][index][subKey] = 0
  }

  // If value is negative → reset to 0
  if (value < 0) {
    formData[key][index][subKey] = 0
  }
}
// Toggle edit mode
const toggleEditMode = () => {
  isEditMode.value = true
}
// Add item to array
const addArrayItem = (key: string) => {
  if (Array.isArray(formData[key]) && isArrayOfStringOrNumber(formData[key])) {
    formData[key].push(typeof formData[key][0] === 'number' ? 0 : '')
  }
}

// Remove item from array
const removeArrayItem = (key: string, index: number) => {
  if (Array.isArray(formData[key]) && formData[key].length > 0) {
    formData[key].splice(index, 1)
  }
}

// Submit edited product data
const submitEdit = async () => {
  console.log(formData)

  // Deep-clone full config (do not strip activation flags; API expects them)
  const sanitizedConfig = prepareImportFdds2ConfigForSave({
    productId,
    formData,
    originalConfig: storedProductData.value,
    syntheticUseIsConditionalOriginal: syntheticUseIsConditionalOriginal.value,
  })

  const objToSend = {
    accountName,
    productName: productId,
    config: sanitizedConfig,
  }
  try {
    await updateProductForAccount(objToSend) // Send updated data to API
    showSnack('Product updated successfully', 'success')
    await new Promise((r) => setTimeout(r, 1500))
    router.push(`/account-admin/${String(accountName)}#products`)
  } catch (error) {
    console.error('Failed to update product:', error)
    showSnack('Error updating product', 'error')
  }
}

// Handle JSON textarea edits
const onJsonChange = (val: string) => {
  try {
    const parsed = JSON.parse(val ?? '{}')
    // Ensure it's an object
    if (parsed && typeof parsed === 'object') {
      Object.keys(formData).forEach((k) => delete formData[k])
      Object.keys(parsed).forEach((k) => (formData[k] = parsed[k]))
      jsonError.value = undefined
    } else {
      jsonError.value = 'JSON must be an object'
    }
  } catch (e: any) {
    jsonError.value = 'Invalid JSON'
  }
}

// Top-level field change handlers
const onTopStringChange = (key: string, val: string) => {
  formData[key] = val
}
const onTopNumberChange = (key: string, val: string | number) => {
  if (val === '' || val === null || typeof val === 'undefined') {
    formData[key] = undefined
    return
  }
  const n = typeof val === 'number' ? val : Number(val)
  formData[key] = Number.isNaN(n) ? val : n
}
const onTopBooleanChange = (key: string, val: boolean) => {
  formData[key] = val
}
const onTopJsonChange = (key: string, val: string) => {
  fieldTextMap[key] = val
  try {
    const parsed = JSON.parse(val)
    formData[key] = parsed
    jsonFieldErrors[key] = undefined
  } catch (e) {
    jsonFieldErrors[key] = 'Invalid JSON'
  }
}

// Copy JSON content
const copyJson = async (e?: Event) => {
  try {
    // prevent focus change/scroll jump from text-area inner icon
    e?.preventDefault?.()
    ;(e as any)?.stopPropagation?.()
  } catch {}
  try {
    await navigator.clipboard.writeText(jsonText.value)
    showSnack('Copied to clipboard', 'info')
  } catch (e) {
    console.warn('Clipboard unavailable')
  }
}

const goBackToProducts = () => {
  router.push(`/account-admin/${String(accountName)}#products`)
}

// Bottom snackbar
const snackbar = ref(false)
const snackbarText = ref('')
const snackbarColor = ref<'success' | 'error' | 'info' | 'warning'>('success')
const showSnack = (text: string, color: 'success' | 'error' | 'info' | 'warning' = 'success') => {
  snackbarText.value = text
  snackbarColor.value = color
  snackbar.value = true
}

// Toggle active state for this product under account
const onToggleActive = async () => {
  try {
    // Backend now handles recursive child disabling automatically
    await toggleProductForAccount(String(accountName), String(productId))
    await loadProductData()
    showSnack(`Product ${productActive.value ? 'disabled' : 'enabled'} successfully`, 'success')
  } catch (e) {
    console.error('Failed to toggle product', e)
    showSnack('Failed to toggle product', 'error')
  }
}

// Confirm dialog state and handlers for toggle
const confirmToggleDialog = ref(false)
const toggleChildren = ref<string[]>([])
const openConfirmToggle = async () => {
  // If disabling, fetch children to show in confirmation
  if (productActive.value) {
    try {
      const [children, accountProducts] = await Promise.all([
        getParentChild(String(productId)),
        fetchAccountsProduct(String(accountName)),
      ])

      if (Array.isArray(children) && children.length > 0 && Array.isArray(accountProducts)) {
        // Create a set of product names/paths that are on the account
        const accountProductSet = new Set(
          accountProducts.map((p: any) => p?.productName || p?.path || p?.productId)
        )

        // Filter to only show children that are actually enrolled on the account
        toggleChildren.value = children
          .filter((c: any) => {
            const childId = c?.productName || c?.path || c?.productId
            return childId && accountProductSet.has(childId)
          })
          .map((c: any) => c?.productFriendlyName || c?.productName || c?.path || '')
          .filter(Boolean)
      } else {
        toggleChildren.value = []
      }
    } catch (e) {
      console.log('Could not fetch children for confirmation dialog')
      toggleChildren.value = []
    }
  } else {
    toggleChildren.value = []
  }
  confirmToggleDialog.value = true
}
const confirmToggle = async () => {
  await onToggleActive()
  confirmToggleDialog.value = false
}

// Remove product dialog and functions
const userStore = useUserStore()
const userRole = computed(() => userStore?.role)
const canRemoveProduct = computed(() =>
  ['company_owner', 'company_suadmin'].includes(String(userRole.value))
)

const removeDialog = ref(false)
const removingProduct = ref(false)
const productChildren = ref<any[]>([])

const openRemoveDialog = async () => {
  // Fetch children to show accurate warning
  try {
    const [children, accountProducts] = await Promise.all([
      getParentChild(String(productId)),
      fetchAccountsProduct(String(accountName)),
    ])

    if (Array.isArray(children) && Array.isArray(accountProducts)) {
      // Create a set of product names/paths that are on the account
      const accountProductSet = new Set(
        accountProducts.map((p: any) => p?.productName || p?.path || p?.productId)
      )

      // Filter to only show children that are actually enrolled on the account
      productChildren.value = children.filter((c: any) => {
        const childId = c?.productName || c?.path || c?.productId
        return childId && accountProductSet.has(childId)
      })
    } else {
      productChildren.value = []
    }
  } catch (error) {
    console.error('Failed to fetch children:', error)
    productChildren.value = []
  }
  removeDialog.value = true
}

const removeProductFromAccount = async () => {
  removingProduct.value = true
  try {
    await deleteProductFromAccount(String(accountName), String(productId))
    showSnack('Product removed successfully', 'success')
    removeDialog.value = false
    await new Promise((r) => setTimeout(r, 1000))
    router.push(`/account-admin/${String(accountName)}#products`)
  } catch (error: any) {
    console.error('Failed to remove product:', error)
    showSnack(error?.response?.data?.message || 'Failed to remove product', 'error')
  } finally {
    removingProduct.value = false
  }
}
// Handle input for number fields in array items
const onNumberInput2 = (key: string, index: number) => {
  let value = formData[key][index]

  if (typeof value === 'string') {
    const parsedValue = parseFloat(value)
    formData[key][index] = !isNaN(parsedValue) ? Math.max(0, parsedValue) : 0
  } else {
    formData[key][index] = Math.max(0, value)
  }
}

// Fetch data on component mount
onMounted(loadProductData)

// Footer visibility: show once data is initialized (active: Save/Cancel, inactive: Done)
const footerVisible = computed(() => dataInitialized.value)

// Track if form has changes by deep-compare against original
const isDirty = computed(() => {
  const current = JSON.parse(JSON.stringify(formData))
  const original = JSON.parse(JSON.stringify(originalData.value || {}))
  // Simple deep equality via deterministic JSON, sufficient for plain data
  return JSON.stringify(current) !== JSON.stringify(original)
})
</script>

<style scoped>
.container {
  max-width: 1200px;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  background-color: #fff;
}

.v-text-field {
  width: 100%;
}

:deep(.v-text-field input) {
  color: black !important;
}

:deep(.v-label) {
  color: black !important;
}

.floating-footer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
  z-index: 10;
}

.subheader-sticky {
  position: sticky;
  top: 64px;
  z-index: 3;
  background: white;
}
</style>
